import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Box, Typography, Divider, Button, FormLabel, TextField, FormControl, SxProps } from "@mui/material";

import { getEntityProfileRoute } from "../../../../../../routers/routeUtils";

import { entityAchievementsEditSchema } from "../../../../schemas/editEntityProfile";
import { EditEntityAchievementsData } from "../../../../types/editEntityProfile-type";
import {
  NationalLeagueIcon,
  NationalCupIcon,
  NationalTournamentIcon,
  InternationalTournamentIcon,
} from "../../../../assets/icons/entity-icons";
import { useEntityAchievements } from "../../../../../Entity/api/queries/useEntityAchievements";
import { useUpdateEntityAchievements } from "../../../../../Entity/api/mutations/useUpdateEntityAchievements";

const achievementsFields = ["nationalLeague", "nationalCup", "nationalTournament", "internationalTournament"] as const;

const achievementsIconMap = {
  nationalLeague: <NationalLeagueIcon />,
  nationalCup: <NationalCupIcon />,
  nationalTournament: <NationalTournamentIcon />,
  internationalTournament: <InternationalTournamentIcon />,
};

const AchievementsTab = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEdit" });
  const navigate = useNavigate();
  const { entityId } = useParams<{ entityId: string }>();

  const { data: achievements } = useEntityAchievements(entityId!);
  const updateAchievements = useUpdateEntityAchievements();

  const methods = useForm<EditEntityAchievementsData>({
    resolver: zodResolver(entityAchievementsEditSchema),
    defaultValues: {
      nationalLeague: achievements?.["National league"]?.count || 0,
      nationalCup: achievements?.["National cup"]?.count || 0,
      nationalTournament: achievements?.["National tournament"]?.count || 0,
      internationalTournament: achievements?.["International tournament"]?.count || 0,
    },
  });

  const onSubmit = (data: EditEntityAchievementsData) => {
    const requestData = [
      {
        id: achievements!["National league"].id,
        count: data.nationalLeague,
      },
      {
        id: achievements!["National cup"].id,
        count: data.nationalCup,
      },
      {
        id: achievements!["National tournament"].id,
        count: data.nationalTournament,
      },
      {
        id: achievements!["International tournament"].id,
        count: data.internationalTournament,
      },
    ];

    updateAchievements.mutate(
      { entityId: entityId!, data: requestData },
      {
        onSuccess: () => {
          navigate(getEntityProfileRoute(entityId!));
        },
      }
    );
  };

  const handleCancel = () => {
    methods.reset();
    navigate(getEntityProfileRoute(entityId!));
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(onSubmit)} sx={styles.formContainer}>
        <Typography sx={styles.title}>{t("achievements")}</Typography>
        <Box sx={styles.achievementsContainer}>
          {achievementsFields.map((fieldName) => (
            <Controller
              key={fieldName}
              name={fieldName}
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <FormControl error={!!error} fullWidth sx={styles.achievementField}>
                  <FormLabel sx={styles.fieldLabel}>
                    {achievementsIconMap[fieldName]}
                    {t(fieldName)}
                  </FormLabel>
                  <Box sx={styles.countContainer}>
                    <Typography sx={styles.countLabel}>{t("numberOfTimes")}</Typography>
                    <TextField
                      type="number"
                      inputProps={{ min: 0 }}
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      error={!!error}
                      helperText={error?.message && t(error.message)}
                      placeholder={t("enterTheNumber")}
                    />
                  </Box>
                </FormControl>
              )}
            />
          ))}
          <Divider orientation="horizontal" sx={styles.divider} />
          <Box sx={styles.buttonsContainer}>
            <Button variant="outlined-sm" type="button" onClick={handleCancel} disabled={updateAchievements.isPending}>
              {t("cancel")}
            </Button>
            <Button variant="primary-sm" type="submit" disabled={updateAchievements.isPending}>
              {t("save")}
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default AchievementsTab;

const styles = {
  formContainer: {
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    padding: "24px 16px",
  } as SxProps,
  title: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "24px",
    marginBottom: "24px",
  } as SxProps,
  achievementsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  } as SxProps,
  achievementField: {
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Base-White, #FFF)",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  } as SxProps,
  fieldLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  } as SxProps,
  countContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  } as SxProps,
  countLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  } as SxProps,
  divider: {
    marginLeft: "-16px",
    marginRight: "-16px",
  } as SxProps,
  buttonsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "16px",
  } as SxProps,
} as const;
